import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Star } from 'lucide-react';
import SaveHeart from './SaveHeart';
import BookCover from '../BookCover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Single tappable poster card used inside every Discover rail.
 *
 * Layouts:
 *  - default: 3:4 portrait (most rails)
 *  - continue: 16:10 landscape, larger, with prominent progress + episode label
 *  - authors: round avatar
 *
 * Whole card is the tap target. Overlay badges (play count, star rating,
 * episode count) render conditionally so the cover dominates when no
 * metadata exists.
 */
const fmtCount = (n) => {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const resolveCover = (src) => {
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  if (src.startsWith('/uploads/')) return `${BACKEND_URL}/api${src}`;
  return src;
};

const onTapNavigate = (item, kind, navigate) => {
  if (kind === 'continue') {
    const id = item.book_id || item.episode_id || item.chapter_id;
    const type = item.type || 'ebook';
    navigate(type === 'ebook' ? `/reader/${id}` : `/player/${id}`);
    return;
  }
  if (kind === 'authors') {
    navigate(`/library?author=${encodeURIComponent(item.author || '')}`);
    return;
  }
  if (kind === 'featured') {
    const id = item.ref_id || item.book_id || item.series_id;
    if (item.is_series) {
      navigate(`/library?focus=${encodeURIComponent(id)}`);
    } else if ((item.type || 'ebook') === 'ebook') {
      navigate(`/reader/${id}`);
    } else {
      navigate(`/player/${id}`);
    }
    return;
  }
  const id = item.book_id || item.series_id;
  const type = item.type || 'ebook';
  if (item.is_series) {
    navigate(`/library?focus=${encodeURIComponent(id)}`);
  } else if (type === 'cinematic' || type === 'audiobook') {
    navigate(`/player/${id}`);
  } else {
    navigate(`/reader/${id}`);
  }
};

// ── Overlay badges (shared across layouts) ─────────────────────────────────

const PlayCountBadge = ({ count, kind }) =>
  count > 0 ? (
    <div
      className={`absolute top-2 right-2 bg-rose-600/95 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-md flex items-center gap-1 shadow-lg ${
        kind === 'continue' ? 'top-3 right-3 text-xs' : ''
      }`}
    >
      <Play className="w-2.5 h-2.5 fill-current" />
      {fmtCount(count)}
    </div>
  ) : null;

// Top-selling rail-only badge: "🔥 N buys" — surfaces social proof on the
// new top-selling-this-week rail. Lives in the bottom-left so it doesn't
// fight with PlayCountBadge in the top-right corner.
const PurchasesBadge = ({ count }) =>
  count > 0 ? (
    <div
      className="absolute bottom-2 left-2 bg-amber-500/95 text-slate-900 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md flex items-center gap-1 shadow-lg"
      data-testid="rail-purchases-badge"
    >
      🔥 {fmtCount(count)} buys
    </div>
  ) : null;

const RatingBadge = ({ rating }) =>
  rating ? (
    <div className="absolute top-2 left-2 bg-slate-900/90 text-amber-300 text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-1 backdrop-blur-sm">
      <Star className="w-2.5 h-2.5 fill-current" />
      {Number(rating).toFixed(1)}
    </div>
  ) : null;

// Stable shared keyboard handler — Enter/Space activates the card.
const onKeyDown = (e, fn) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn();
  }
};

// ── Continue-rail card (landscape, prominent progress) ────────────────────

const ContinueCard = ({ item, onTap }) => {
  const progress = Math.min(100, Math.max(0, Number(item.progress) || 0));
  const epLabel = item.episode_number
    ? `Ep ${item.episode_number}`
    : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => onKeyDown(e, onTap)}
      className="group flex-shrink-0 w-[260px] sm:w-[320px] lg:w-[360px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-xl will-change-transform transition-transform duration-200 hover:-translate-y-1 cursor-pointer"
      data-testid={`rail-card-${item.book_id}`}
    >
      <div className="relative aspect-[16/10] rounded-xl overflow-hidden ring-1 ring-slate-700/50 group-hover:ring-amber-400/60 group-hover:shadow-2xl group-hover:shadow-amber-500/15 transition-all">
        <BookCover
          book={item}
          className="h-full w-full"
          imageClassName="transition-transform duration-300 group-hover:scale-[1.04] motion-reduce:transform-none"
        />
        {/* Bottom gradient for legibility of resume label */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-3 px-3 sm:px-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-amber-300/95 mb-1">
            <Play className="w-3 h-3 fill-current" /> Resume
            {epLabel && <span className="text-white/80">· {epLabel}</span>}
            <span className="text-white/80">· {Math.round(progress)}%</span>
          </div>
          <h4 className="text-sm sm:text-base text-white font-semibold line-clamp-1">
            {item.series_title || item.title}
          </h4>
          {item.series_title && item.title && item.series_title !== item.title && (
            <p className="text-[11px] text-slate-300 line-clamp-1">{item.title}</p>
          )}
        </div>
        {/* Progress bar pinned to the very bottom */}
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-black/40">
          <div
            className="h-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]"
            style={{ width: `${Math.max(progress, 2)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ── Author avatar card ────────────────────────────────────────────────────

const AuthorCard = ({ item, onTap }) => {
  const cover = resolveCover(item.cover_image);
  return (
    <button
      onClick={onTap}
      className="group flex-shrink-0 w-[120px] sm:w-[140px] flex flex-col items-center gap-2 focus:outline-none"
      data-testid={`rail-card-author-${item.author}`}
    >
      <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-700 group-hover:border-amber-400 transition-colors">
        {cover ? (
          <img src={cover} alt={item.author} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500 text-xl">
            {(item.author || '?').slice(0, 1)}
          </div>
        )}
      </div>
      <div className="text-xs text-slate-200 text-center font-medium truncate w-full">
        {item.author}
      </div>
      {item.plays > 0 && (
        <div className="text-[10px] text-rose-300/90 font-bold">{fmtCount(item.plays)} plays</div>
      )}
    </button>
  );
};

// ── Default portrait card ─────────────────────────────────────────────────

const DefaultCard = ({ item, onTap, size, kind, savedSet, onSaveChange }) => {
  const widths = {
    sm: 'w-[120px] sm:w-[140px]',
    md: 'w-[150px] sm:w-[180px] lg:w-[200px]',
    lg: 'w-[200px] sm:w-[240px] lg:w-[280px]',
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => onKeyDown(e, onTap)}
      className={`group flex-shrink-0 ${widths[size] || widths.md} text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-xl will-change-transform transition-transform duration-200 hover:-translate-y-1 cursor-pointer`}
      data-testid={`rail-card-${item.ref_id || item.book_id || item.series_id}`}
    >
      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-slate-800 ring-1 ring-slate-700/50 group-hover:ring-amber-400/60 group-hover:shadow-2xl group-hover:shadow-amber-500/10 transition-all">
        <BookCover
          book={item}
          className="h-full w-full"
          imageClassName="transition-transform duration-300 group-hover:scale-[1.06] motion-reduce:transform-none"
        />

        {/* Hover play overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="w-12 h-12 rounded-full bg-white/95 text-slate-900 flex items-center justify-center shadow-lg">
            <Play className="w-5 h-5 fill-current ml-0.5" />
          </div>
        </div>

        <PlayCountBadge count={item.play_count} />
        <RatingBadge rating={item.rating} />
        {/* Top-selling rail surfaces 🔥 N buys to provide social proof. */}
        {kind === 'top_selling' && <PurchasesBadge count={item.purchases_7d} />}

        {item.is_series && item.episode_count > 0 && (
          <div className="absolute bottom-2 left-2 bg-slate-900/90 text-slate-100 text-[10px] font-semibold px-1.5 py-0.5 rounded">
            {item.episode_count} eps
          </div>
        )}

        {/* Featured-rail tagline overlay if the admin set one */}
        {kind === 'featured' && item.tagline && (
          <div className="absolute inset-x-0 bottom-0 p-2 pt-6 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent">
            <p className="text-[11px] text-amber-200/90 font-medium line-clamp-2">
              {item.tagline}
            </p>
          </div>
        )}

        {/* Save / heart toggle. Hidden on the authors rail and on the
            Continue card variant (Continue uses ContinueCard, not Default). */}
        {kind !== 'authors' && (
          <SaveHeart item={item} savedSet={savedSet} onChange={onSaveChange} />
        )}
      </div>

      <div className="mt-2 px-0.5">
        <h4 className="text-sm text-slate-200 font-medium line-clamp-1 group-hover:text-white transition-colors">
          {item.title}
        </h4>
        {item.author && (
          <p className="text-[11px] text-slate-500 line-clamp-1">{item.author}</p>
        )}
      </div>
    </div>
  );
};

// ── Dispatcher ────────────────────────────────────────────────────────────

const RailCard = ({ item, kind = 'default', size = 'md', savedSet, onSaveChange }) => {
  const navigate = useNavigate();
  const onTap = () => onTapNavigate(item, kind, navigate);

  if (kind === 'authors') return <AuthorCard item={item} onTap={onTap} />;
  if (kind === 'continue') return <ContinueCard item={item} onTap={onTap} />;
  return (
    <DefaultCard
      item={item}
      onTap={onTap}
      size={size}
      kind={kind}
      savedSet={savedSet}
      onSaveChange={onSaveChange}
    />
  );
};

export default RailCard;
